import axios from 'axios';
import { toast } from 'react-toastify';

// Use runtime config from window._env_ if available, otherwise fall back to build-time env
const API_URL = (window._env_ && window._env_.REACT_APP_API_URL) ||
                process.env.REACT_APP_API_URL ||
                'http://localhost:8000';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken');
      toast.error('Your session has expired. Please log in again.');
      // Redirect to login page (only if not already there)
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      toast.error(error.response?.data?.detail || 'Access denied');
    } else if (error.response?.status === 404) {
      toast.error(error.response?.data?.detail || 'Resource not found');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.code === 'ERR_NETWORK') {
      toast.error('Network error. Please check your connection.');
    }
    // Don't show toast for 4xx errors handled by components
    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // Authentication
  login: async (username, password) => {
    const response = await axiosInstance.post('/api/auth/login', {
      username,
      password,
    });
    return response.data;
  },

  register: async (username, email, password, displayName) => {
    const response = await axiosInstance.post('/api/auth/register', {
      username,
      email,
      password,
      display_name: displayName,
    });
    return response.data;
  },

  logout: async () => {
    const response = await axiosInstance.post('/api/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await axiosInstance.get('/api/auth/me');
    return response.data;
  },

  updateUser: async (updateData) => {
    const response = await axiosInstance.patch('/api/auth/me', updateData);
    return response.data;
  },
  // Health check
  getHealth: async () => {
    const response = await axiosInstance.get('/health');
    return response.data;
  },

  // Projects
  getProjects: async (params = {}) => {
    const {
      skip = 0,
      limit = 100,
      domain = null,
      date_from = null,
      date_to = null,
      sort_by = 'created_at',
      sort_order = 'desc',
    } = params;

    const queryParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      sort_by,
      sort_order,
    });

    if (domain) queryParams.append('domain', domain);
    if (date_from) queryParams.append('date_from', date_from);
    if (date_to) queryParams.append('date_to', date_to);

    const response = await axiosInstance.get(`/api/projects?${queryParams.toString()}`);
    return response.data;
  },

  getProject: async (id) => {
    const response = await axiosInstance.get(`/api/projects/${id}`);
    return response.data;
  },

  createProject: async (projectData) => {
    const response = await axiosInstance.post('/api/projects', projectData);
    return response.data;
  },

  updateProject: async (id, projectData) => {
    const response = await axiosInstance.put(`/api/projects/${id}`, projectData);
    return response.data;
  },

  deleteProject: async (id) => {
    await axiosInstance.delete(`/api/projects/${id}`);
  },

  // File uploads
  uploadFile: async (projectId, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post(
      `/api/projects/${projectId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onUploadProgress,
      }
    );
    return response.data;
  },

  getProjectFiles: async (projectId) => {
    const response = await axiosInstance.get(`/api/projects/${projectId}/files`);
    return response.data;
  },

  deleteFile: async (fileId) => {
    await axiosInstance.delete(`/api/files/${fileId}`);
  },

  // Project membership (FR-18.1/18.3)
  getProjectMembers: async (projectId, includeRemoved = false) => {
    const response = await axiosInstance.get(
      `/api/projects/${projectId}/members`,
      { params: { include_removed: includeRemoved } }
    );
    return response.data;
  },

  addProjectMember: async (projectId, { userId, username }) => {
    const response = await axiosInstance.post(
      `/api/projects/${projectId}/members`,
      { user_id: userId ?? null, username: username ?? null }
    );
    return response.data;
  },

  removeProjectMember: async (projectId, userId) => {
    const response = await axiosInstance.delete(
      `/api/projects/${projectId}/members/${userId}`
    );
    return response.data;
  },

  uploadChunk: async (projectId, chunk, chunkNumber, totalChunks, uploadId, originalFilename) => {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('chunk_number', chunkNumber);
    formData.append('total_chunks', totalChunks);
    formData.append('upload_id', uploadId);
    formData.append('original_filename', originalFilename);

    const response = await axiosInstance.post(
      `/api/projects/${projectId}/upload/chunk`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Transcriptions
  // useContext=false runs a discovery pass (FR-19.5): no interview context of
  // any kind reaches the provider, so the proper nouns that come back are the
  // model's own and can seed the Context Packet. Cannot be combined with an
  // explicit prompt; the API refuses that with 400.
  startTranscription: async (projectId, audioFileId, provider = 'openai', model = null, language = null, prompt = null, useContext = true) => {
    const response = await axiosInstance.post(
      `/api/projects/${projectId}/transcribe`,
      {
        audio_file_id: audioFileId,
        provider: provider,
        model: model,
        language: language,
        prompt: prompt,
        use_context: useContext,
      }
    );
    return response.data;
  },

  getProviderModels: async (provider = 'openai') => {
    const response = await axiosInstance.get(`/api/providers/${provider}/models`);
    return response.data;
  },

  getTranscription: async (transcriptId) => {
    const response = await axiosInstance.get(`/api/transcriptions/${transcriptId}`);
    return response.data;
  },

  getTaskStatus: async (taskId) => {
    const response = await axiosInstance.get(`/api/transcriptions/task/${taskId}`);
    return response.data;
  },

  getProjectTranscriptions: async (projectId) => {
    const response = await axiosInstance.get(`/api/projects/${projectId}/transcriptions`);
    return response.data;
  },

  // FR-20.2: the same listing, plus what the body cannot say about itself.
  // The server caps this response and used to do so silently; at 89 recordings
  // with two or more passes each the pilot crosses that cap during production,
  // and a truncated list is indistinguishable from a complete one. Kept
  // separate from getProjectTranscriptions so existing callers are untouched.
  getProjectTranscriptionsPage: async (projectId) => {
    const response = await axiosInstance.get(`/api/projects/${projectId}/transcriptions`);
    const asInt = (header, fallback) => {
      const value = parseInt(response.headers?.[header], 10);
      return Number.isNaN(value) ? fallback : value;
    };
    const items = response.data;
    return {
      items,
      // Absent headers mean an older backend; report no truncation rather
      // than inventing a total, so the banner stays quiet instead of wrong.
      total: asInt('x-total-count', items.length),
      limit: asInt('x-result-limit', items.length),
      offset: asInt('x-result-offset', 0),
    };
  },

  deleteTranscription: async (transcriptId) => {
    await axiosInstance.delete(`/api/transcriptions/${transcriptId}`);
  },

  // `updateData` MUST carry base_revision (FR-18.6): the server answers 428
  // without it and 409 if another member has saved since. Callers should let
  // the 409 propagate and surface it rather than retrying, so that nobody's
  // corrections are overwritten by an automatic second attempt.
  updateTranscription: async (transcriptId, updateData) => {
    const response = await axiosInstance.put(`/api/transcriptions/${transcriptId}`, updateData);
    return response.data;
  },

  // Bulk speaker relabelling (FR-7.1, FR-7.2). One call for a whole
  // interview's worth of renames and merges, rather than a full-transcript
  // write per label. `mapping` is {oldLabel: newLabel}; two keys sharing a
  // value merge those speakers. Like updateTranscription, a 409 is allowed to
  // propagate rather than retried.
  remapSpeakers: async (transcriptId, mapping, baseRevision) => {
    const response = await axiosInstance.post(
      `/api/transcriptions/${transcriptId}/speakers`,
      { mapping, base_revision: baseRevision },
    );
    return response.data;
  },

  // Advisory editing presence (FR-18.10). Never blocks; a failure here must
  // not interrupt editing, so callers swallow its errors.
  // `beat` is the optional FR-17.6.2 interaction report. Omitting it keeps the
  // pre-FR-17.6 behaviour (presence only, no measurement recorded).
  recordEditingActivity: async (transcriptId, beat = undefined) => {
    const response = await axiosInstance.post(
      `/api/transcriptions/${transcriptId}/activity`,
      beat,
    );
    return response.data;
  },

  getEditingActivity: async (transcriptId) => {
    const response = await axiosInstance.get(
      `/api/transcriptions/${transcriptId}/activity`
    );
    return response.data;
  },

  exportTranscription: async (transcriptId, exportOptions) => {
    const response = await axiosInstance.post(
      `/api/transcriptions/${transcriptId}/export`,
      exportOptions,
      {
        responseType: 'blob', // Important for file download
      }
    );
    return response;
  },

  // FR-21.10: what the export will contain, as data. Same endpoint, same
  // options and same traversal as the file, so the two cannot disagree.
  previewExport: async (transcriptId, exportOptions = {}) => {
    const response = await axiosInstance.post(
      `/api/transcriptions/${transcriptId}/export`,
      { ...exportOptions, format: 'preview' }
    );
    return response.data;
  },

  // Context management
  getProjectContext: async (projectId) => {
    const response = await axiosInstance.get(`/api/projects/${projectId}/context`);
    return response.data;
  },

  updateProjectContext: async (projectId, contextData) => {
    const response = await axiosInstance.put(`/api/projects/${projectId}/context`, contextData);
    return response.data;
  },

  // Per-recording context (FR-19). Returns the recording's own overrides, the
  // effective values after inheriting from the project, and which fields are
  // overridden -- the UI needs all three to distinguish "empty" from
  // "inherited".
  getRecordingContext: async (audioFileId) => {
    const response = await axiosInstance.get(`/api/audio/${audioFileId}/context`);
    return response.data;
  },

  updateRecordingContext: async (audioFileId, contextData) => {
    const response = await axiosInstance.put(`/api/audio/${audioFileId}/context`, contextData);
    return response.data;
  },

  getDomains: async () => {
    const response = await axiosInstance.get('/api/domains');
    return response.data;
  },

  getDomain: async (domainId) => {
    const response = await axiosInstance.get(`/api/domains/${domainId}`);
    return response.data;
  },

  previewPrompt: async (promptData) => {
    const response = await axiosInstance.post('/api/domains/preview', promptData);
    return response.data;
  },

  // Audio streaming
  getAudioStreamUrl: (audioFileId) => {
    return `${API_URL}/api/audio/${audioFileId}/stream`;
  },

  // Everything the player needs to start without downloading audio: a
  // tokenised URL a media element can stream from, the precomputed waveform
  // peaks, and the duration they span.
  getPlaybackInfo: async (audioFileId) => {
    const response = await axiosInstance.get(`/api/audio/${audioFileId}/playback-info`);
    return { ...response.data, url: `${API_URL}${response.data.url}` };
  },

  getAudioMetadata: async (audioFileId) => {
    const response = await axiosInstance.get(`/api/audio/${audioFileId}/metadata`);
    return response.data;
  },

  // Search
  globalSearch: async (query, params = {}) => {
    const {
      domain = null,
      project_id = null,
      limit = 20,
      offset = 0,
    } = params;

    const queryParams = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (domain) queryParams.append('domain', domain);
    if (project_id) queryParams.append('project_id', project_id);

    const response = await axiosInstance.get(`/api/search?${queryParams.toString()}`);
    return response.data;
  },

  searchInTranscript: async (transcriptId, query) => {
    const response = await axiosInstance.get(
      `/api/transcriptions/${transcriptId}/search?q=${encodeURIComponent(query)}`
    );
    return response.data;
  },

  // Transcript Comparisons
  createComparison: async (transcriptAId, transcriptBId, options = {}) => {
    const response = await axiosInstance.post('/api/comparisons/', {
      transcript_a_id: transcriptAId,
      transcript_b_id: transcriptBId,
      options: {
        normalize_speaker_labels: options.normalize_speaker_labels ?? false,
        normalize_timestamps: options.normalize_timestamps ?? false,
        normalize_punctuation: options.normalize_punctuation ?? true,
        case_sensitive: options.case_sensitive ?? false,
        expand_contractions: options.expand_contractions ?? false,
        reference_transcript: options.reference_transcript || null,
      },
    });
    return response.data;
  },

  getComparison: async (comparisonId) => {
    const response = await axiosInstance.get(`/api/comparisons/${comparisonId}`);
    return response.data;
  },

  getAudioTranscripts: async (audioFileId) => {
    const response = await axiosInstance.get(`/api/comparisons/audio/${audioFileId}/transcripts`);
    return response.data;
  },

  // Manual/Reference Transcripts (Sprint 12)
  createManualTranscript: async (audioFileId, transcriptData) => {
    const response = await axiosInstance.post(
      `/api/audio/${audioFileId}/transcripts/manual`,
      transcriptData
    );
    return response.data;
  },

  uploadReferenceTranscript: async (audioFileId, file, isReference = true) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post(
      `/api/audio/${audioFileId}/transcripts/upload?is_reference=${isReference}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Version Control
  getAudioFileVersions: async (audioFileId, latestOnly = false) => {
    const params = latestOnly ? '?include_latest_only=true' : '';
    const response = await axiosInstance.get(`/api/audio-files/${audioFileId}/versions${params}`);
    return response.data;
  },

  // FR-20.3: every pass over one recording, newest first, annotated with who
  // ran it, whether it was blind or context-aided, and whether it still holds
  // review comments. Includes queued, running and failed runs, which
  // getAudioFileVersions deliberately omits.
  getRecordingVersionGroup: async (audioFileId, includeHidden = false) => {
    const response = await axiosInstance.get(
      `/api/audio-files/${audioFileId}/version-group`,
      { params: includeHidden ? { include_hidden: true } : {} }
    );
    return response.data;
  },

  // FR-20.7: declare this version the recording's gold copy. From here it is
  // locked against editing and is the baseline the metrics measure against.
  finalizeTranscription: async (transcriptId) => {
    const response = await axiosInstance.post(
      `/api/transcriptions/${transcriptId}/finalize`
    );
    return response.data;
  },

  unfinalizeTranscription: async (transcriptId) => {
    const response = await axiosInstance.delete(
      `/api/transcriptions/${transcriptId}/finalize`
    );
    return response.data;
  },

  // FR-20.8: bring back a run that was put away. The hiding half is
  // deleteTranscription, which is a soft hide on the server.
  restoreTranscription: async (transcriptId) => {
    const response = await axiosInstance.post(
      `/api/transcriptions/${transcriptId}/restore`
    );
    return response.data;
  },

  // FR-20.2 / FR-20.7: how much of the collection is done, counted against
  // every recording deposited in it.
  getProjectProgress: async (projectId) => {
    const response = await axiosInstance.get(`/api/projects/${projectId}/progress`);
    return response.data;
  },

  getTranscriptVersions: async (transcriptId) => {
    const response = await axiosInstance.get(`/api/transcripts/${transcriptId}/versions`);
    return response.data;
  },

  getVersion: async (versionId) => {
    const response = await axiosInstance.get(`/api/versions/${versionId}`);
    return response.data;
  },

  createManualVersion: async (transcriptId, comment = null) => {
    const response = await axiosInstance.post(`/api/transcripts/${transcriptId}/versions`, {
      comment: comment,
    });
    return response.data;
  },

  revertToVersion: async (transcriptId, targetVersionId) => {
    const response = await axiosInstance.post(`/api/transcripts/${transcriptId}/revert`, {
      target_version_id: targetVersionId,
    });
    return response.data;
  },

  compareVersions: async (versionAId, versionBId) => {
    const response = await axiosInstance.get(`/api/versions/compare?version_a_id=${versionAId}&version_b_id=${versionBId}`);
    return response.data;
  },

  // Comments
  createComment: async (transcriptId, commentData) => {
    const response = await axiosInstance.post(`/api/transcripts/${transcriptId}/comments`, commentData);
    return response.data;
  },

  getTranscriptComments: async (transcriptId, includeResolved = true, commentType = null) => {
    const params = new URLSearchParams();
    params.append('include_resolved', includeResolved.toString());
    if (commentType) params.append('comment_type', commentType);

    const response = await axiosInstance.get(`/api/transcripts/${transcriptId}/comments?${params.toString()}`);
    return response.data;
  },

  getSegmentComments: async (transcriptId, segmentIndex, includeResolved = true) => {
    const params = includeResolved ? '' : '?include_resolved=false';
    const response = await axiosInstance.get(`/api/transcripts/${transcriptId}/segments/${segmentIndex}/comments${params}`);
    return response.data;
  },

  getComment: async (commentId) => {
    const response = await axiosInstance.get(`/api/comments/${commentId}`);
    return response.data;
  },

  updateComment: async (commentId, updateData) => {
    const response = await axiosInstance.patch(`/api/comments/${commentId}`, updateData);
    return response.data;
  },

  deleteComment: async (commentId) => {
    await axiosInstance.delete(`/api/comments/${commentId}`);
  },

  markSegmentForReview: async (transcriptId, segmentIndex, reason, authorName = null) => {
    const response = await axiosInstance.post(`/api/transcripts/${transcriptId}/review`, {
      segment_index: segmentIndex,
      reason: reason,
      author_name: authorName,
    });
    return response.data;
  },

  getReviewSummary: async (transcriptId) => {
    const response = await axiosInstance.get(`/api/transcripts/${transcriptId}/review-summary`);
    return response.data;
  },

  // Admin endpoints
  getSystemStats: async () => {
    const response = await axiosInstance.get('/api/admin/stats');
    return response.data;
  },

  listUsers: async (params = {}) => {
    const { skip = 0, limit = 100, search = null, is_active = null, is_admin = null } = params;
    const queryParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    if (search) queryParams.append('search', search);
    if (is_active !== null) queryParams.append('is_active', is_active.toString());
    if (is_admin !== null) queryParams.append('is_admin', is_admin.toString());

    const response = await axiosInstance.get(`/api/admin/users?${queryParams.toString()}`);
    return response.data;
  },

  getUser: async (userId) => {
    const response = await axiosInstance.get(`/api/admin/users/${userId}`);
    return response.data;
  },

  createUser: async (userData) => {
    const response = await axiosInstance.post('/api/admin/users', userData);
    return response.data;
  },

  updateUser: async (userId, updateData) => {
    const response = await axiosInstance.put(`/api/admin/users/${userId}`, updateData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await axiosInstance.delete(`/api/admin/users/${userId}`);
    return response.data;
  },

  resetUserPassword: async (userId, password = null) => {
    const response = await axiosInstance.post(`/api/admin/users/${userId}/reset-password`, {
      password: password,
    });
    return response.data;
  },
};

export default apiService;
